import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HttpIcon from '@mui/icons-material/Http';
import AddIcon from '@mui/icons-material/Add';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InputAdornment from '@mui/material/InputAdornment';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Papa from 'papaparse';
import axios from 'axios';
import Grid2 from '@mui/material/Grid2';
import PageHeader from '../../components/PageHeader';
import { themeBackgrounds } from '../../theme/theme';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

type UpdateSection = 'details' | 'faq' | 'faq-list' | 'webhooks' | 'webhook-form';

export interface ChatBotToUpdate {
  id: string;
  bot_id: string;
  bot_name: string;
  welcome_message: string;
  no_result_message: string;
  handover_message: string;
}

interface UpdateChatBotProps {
  bot: ChatBotToUpdate;
  onCancel: () => void;
  onUpdated: () => void;
}

interface ChatBotDetails {
  bot_name: string;
  welcome_message: string;
  no_result_message: string;
  handover_message: string;
}

interface FaqRow {
  category: string;
  question: string;
  answer: string;
  keyword: string;
  url: string;
  url_label: string;
  image_url: string;
}

interface FaqQuestionItem {
  question_id: string;
  category: string;
  question: string;
  answer: string;
  keyword: string;
  url: string;
  url_label: string;
  image_url: string;
  question_type: string;
  intent: string;
  sub_questions_ids: string[];
  sub_questions_count: number;
  webhook_id: string;
  webhook_name: string;
}

interface FaqFormState {
  category: string;
  question: string;
  answer: string;
  keyword: string;
  url: string;
  url_label: string;
  image_url: string;
  question_type: 'static' | 'dynamic';
  webhook_id: string;
  webhook_name: string;
  sub_questions_ids: string[];
}

interface PendingIntentUpdate {
  webhook_id: string;
  webhook_name: string;
}

interface WebhookItem {
  webhook_id: string;
  webhook_name: string;
  method: string;
  url: string;
  headers: Record<string, unknown>;
  body: Record<string, unknown>;
  response_map: Record<string, unknown>;
  status: string;
}

interface WebhookFormState {
  webhook_name: string;
  method: string;
  url: string;
  headersText: string;
  bodyText: string;
  responseMapText: string;
}

interface UpdateChatBotResponse {
  status: number;
  message?: string;
  data?: any;
}

const WEBHOOK_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
const FAQ_QUESTION_TYPES = ['static', 'dynamic'] as const;
const ADD_NEW_CATEGORY_VALUE = '__add_new_category__';
const WEBHOOK_PLACEHOLDER_REGEX = /\{\{\s*([^}]+?)\s*\}\}/g;

const emptyFaqForm = (): FaqFormState => ({
  category: '',
  question: '',
  answer: '',
  keyword: '',
  url: '',
  url_label: '',
  image_url: '',
  question_type: 'static',
  webhook_id: '',
  webhook_name: '',
  sub_questions_ids: [],
});

const emptyWebhookForm = (): WebhookFormState => ({
  webhook_name: '',
  method: 'POST',
  url: '',
  headersText: '{\n  \n}',
  bodyText: '{\n  \n}',
  responseMapText: '{\n  \n}',
});

const toPrettyJson = (value: unknown): string => {
  try {
    return JSON.stringify(value && typeof value === 'object' ? value : {}, null, 2);
  } catch {
    return '{\n  \n}';
  }
};

const collectWebhookPlaceholders = (value: unknown, keys: Set<string>): void => {
  if (typeof value === 'string') {
    const regex = new RegExp(WEBHOOK_PLACEHOLDER_REGEX);
    let match = regex.exec(value);
    while (match) {
      const key = String(match[1] || '').trim();
      if (key) keys.add(key);
      match = regex.exec(value);
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectWebhookPlaceholders(item, keys));
    return;
  }

  if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach((item) =>
      collectWebhookPlaceholders(item, keys)
    );
  }
};

const getWebhookPlaceholderFieldName = (placeholderKey: string): string => {
  const parts = placeholderKey.split('.').filter(Boolean);
  return parts[parts.length - 1] || placeholderKey;
};

const replaceWebhookPlaceholders = (
  value: unknown,
  valuesByKey: Record<string, string>
): unknown => {
  if (typeof value === 'string') {
    return value.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_full, key: string) => {
      const trimmed = String(key || '').trim();
      return valuesByKey[trimmed] ?? '';
    });
  }

  if (Array.isArray(value)) {
    return value.map((item) => replaceWebhookPlaceholders(item, valuesByKey));
  }

  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([entryKey, entryValue]) => {
      result[entryKey] = replaceWebhookPlaceholders(entryValue, valuesByKey);
    });
    return result;
  }

  return value;
};

const extractWebhookTestPlaceholders = (webhook: WebhookItem): string[] => {
  const keys = new Set<string>();
  collectWebhookPlaceholders(webhook.url, keys);
  collectWebhookPlaceholders(webhook.headers || {}, keys);
  if (String(webhook.method || '').toUpperCase() !== 'GET') {
    collectWebhookPlaceholders(webhook.body || {}, keys);
  }
  return Array.from(keys);
};

const parseJsonObject = (raw: string, fieldLabel: string): Record<string, unknown> | null => {
  const trimmed = raw.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      toast.error(`${fieldLabel} must be a JSON object.`, { position: 'top-right' });
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    toast.error(`${fieldLabel} contains invalid JSON.`, { position: 'top-right' });
    return null;
  }
};

const mapWebhookItem = (item: any, index: number): WebhookItem => ({
  webhook_id: String(item.webhook_id || item._id || item.id || `webhook-${index}`),
  webhook_name: item.webhook_name || item.name || '',
  method: String(item.method || 'POST').toUpperCase(),
  url: item.url || '',
  headers: item.headers && typeof item.headers === 'object' ? item.headers : {},
  body: item.body && typeof item.body === 'object' ? item.body : {},
  response_map: item.response_map && typeof item.response_map === 'object' ? item.response_map : {},
  status: item.status || item.webhook_status || 'InActive',
});

const extractApisFromDecrypted = (decrypted: any): any[] => {
  if (Array.isArray(decrypted?.chatBot?.apis)) return decrypted.chatBot.apis;
  if (Array.isArray(decrypted?.chat_bot?.apis)) return decrypted.chat_bot.apis;
  if (Array.isArray(decrypted?.apis)) return decrypted.apis;
  return [];
};

const extractBotsFromDecrypted = (decrypted: any): any[] => {
  if (Array.isArray(decrypted)) return decrypted;
  if (Array.isArray(decrypted?.chat_bots)) return decrypted.chat_bots;
  if (Array.isArray(decrypted?.chatBots)) return decrypted.chatBots;
  if (Array.isArray(decrypted?.bots)) return decrypted.bots;
  if (Array.isArray(decrypted?.data)) return decrypted.data;
  return [];
};

const getCsvCell = (row: Record<string, unknown>, keys: string[]): string => {
  const normalized: Record<string, string> = {};
  Object.keys(row || {}).forEach((key) => {
    const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, '_');
    normalized[normalizedKey] = String(row[key] ?? '').trim();
  });

  for (const key of keys) {
    if (normalized[key]) {
      return normalized[key];
    }
  }
  return '';
};

const mapCsvRowToFaq = (row: Record<string, unknown>): FaqRow => ({
  category: getCsvCell(row, ['category']),
  question: getCsvCell(row, ['question']),
  answer: getCsvCell(row, ['answer']),
  keyword: getCsvCell(row, ['keyword', 'keywords']),
  url: getCsvCell(row, ['url']),
  url_label: getCsvCell(row, ['url_label', 'urllabel']),
  image_url: getCsvCell(row, ['image_url', 'imageurl']),
});

const UpdateChatBot: React.FC<UpdateChatBotProps> = ({ bot, onCancel, onUpdated }) => {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationDetails = useSelector((state: any) => state.applicationDetails);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeSection, setActiveSection] = useState<UpdateSection>('details');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUploadingFaq, setIsUploadingFaq] = useState(false);
  const [faqFileName, setFaqFileName] = useState('');
  const [faqs, setFaqs] = useState<FaqRow[]>([]);
  const [faqPreviewPage, setFaqPreviewPage] = useState(0);
  const [faqQuestions, setFaqQuestions] = useState<FaqQuestionItem[]>([]);
  const [faqCategories, setFaqCategories] = useState<string[]>([]);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [faqListPage, setFaqListPage] = useState(0);
  const [isLoadingFaqQuestions, setIsLoadingFaqQuestions] = useState(false);
  const [isDeletingFaq, setIsDeletingFaq] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<FaqQuestionItem | null>(null);
  const [selectedFaqIds, setSelectedFaqIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeletingSelectedFaqs, setIsDeletingSelectedFaqs] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [webhooks, setWebhooks] = useState<WebhookItem[]>([]);
  const [isLoadingWebhooks, setIsLoadingWebhooks] = useState(false);
  const [webhookForm, setWebhookForm] = useState<WebhookFormState>(emptyWebhookForm);
  const [editingWebhookId, setEditingWebhookId] = useState<string | null>(null);
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<WebhookItem | null>(null);
  const [isDeletingWebhook, setIsDeletingWebhook] = useState(false);
  const [webhookActionAnchor, setWebhookActionAnchor] = useState<null | HTMLElement>(null);
  const [webhookActionTarget, setWebhookActionTarget] = useState<WebhookItem | null>(null);
  const [webhookTestDialogOpen, setWebhookTestDialogOpen] = useState(false);
  const [webhookUnderTest, setWebhookUnderTest] = useState<WebhookItem | null>(null);
  const [webhookTestPlaceholders, setWebhookTestPlaceholders] = useState<string[]>([]);
  const [webhookTestValues, setWebhookTestValues] = useState<Record<string, string>>({});
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState<string>('');
  const [isUpdatingWebhookStatus, setIsUpdatingWebhookStatus] = useState(false);
  const [faqActionAnchor, setFaqActionAnchor] = useState<null | HTMLElement>(null);
  const [faqActionTarget, setFaqActionTarget] = useState<FaqQuestionItem | null>(null);
  const [subQuestionsDialogOpen, setSubQuestionsDialogOpen] = useState(false);
  const [intentDialogOpen, setIntentDialogOpen] = useState(false);
  const [draftSubQuestionIds, setDraftSubQuestionIds] = useState<string[]>([]);
  const [subQuestionSearch, setSubQuestionSearch] = useState('');
  const [draftWebhookId, setDraftWebhookId] = useState('');
  const [pendingSubQuestionUpdates, setPendingSubQuestionUpdates] = useState<Record<string, string[]>>({});
  const [pendingIntentUpdates, setPendingIntentUpdates] = useState<Record<string, PendingIntentUpdate>>({});
  const [isSubmittingFaqUpdates, setIsSubmittingFaqUpdates] = useState(false);
  const [faqFormDialogOpen, setFaqFormDialogOpen] = useState(false);
  const [editingFaqQuestionId, setEditingFaqQuestionId] = useState<string | null>(null);
  const [faqForm, setFaqForm] = useState<FaqFormState>(emptyFaqForm);
  const [isSavingFaqForm, setIsSavingFaqForm] = useState(false);
  const [details, setDetails] = useState<ChatBotDetails>({
    bot_name: bot.bot_name || '',
    welcome_message: bot.welcome_message || '',
    no_result_message: bot.no_result_message || '',
    handover_message: bot.handover_message || '',
  });

  const handleChange = (field: keyof ChatBotDetails) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setDetails((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const isValid = Boolean(details.bot_name.trim());

  const handleSectionChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: UpdateSection | null
  ) => {
    if (!value) return;
    if (value === 'webhook-form' && activeSection !== 'webhook-form') {
      setEditingWebhookId(null);
      setWebhookForm(emptyWebhookForm());
    }
    if (value === 'webhooks') {
      fetchWebhooks();
    }
    setActiveSection(value);
  };

  const extractFaqQuestions = (decrypted: any): any[] => {
    if (Array.isArray(decrypted)) return decrypted;
    if (Array.isArray(decrypted?.faqs)) return decrypted.faqs;
    if (Array.isArray(decrypted?.questions)) return decrypted.questions;
    if (Array.isArray(decrypted?.chat_bot_faqs)) return decrypted.chat_bot_faqs;
    if (Array.isArray(decrypted?.data)) return decrypted.data;
    return [];
  };

  const extractFaqCategories = (decrypted: any, faqs: FaqQuestionItem[]): string[] => {
    const fromApiRaw =
      (Array.isArray(decrypted?.categories) && decrypted.categories) ||
      (Array.isArray(decrypted?.chatBot?.categories) && decrypted.chatBot.categories) ||
      (Array.isArray(decrypted?.chat_bot?.categories) && decrypted.chat_bot.categories) ||
      [];
    const fromApi = fromApiRaw.map((item: unknown) => String(item || '').trim()).filter(Boolean);
    const fromFaqs = faqs.map((item) => String(item.category || '').trim()).filter(Boolean);
    return Array.from(new Set([...fromApi, ...fromFaqs])).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  };

  const fetchFaqQuestions = async (): Promise<void> => {
    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId) {
      setFaqQuestions([]);
      setFaqCategories([]);
      return;
    }

    setIsLoadingFaqQuestions(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_id: botId,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/getChatBotFaqs`,
        payload
      );

      if (response.data?.status === 200 && response.data.data) {
        const decrypted = await decryptResponse<any>(
          response.data.data,
          getSessionKey() as string
        );
        const rows = extractFaqQuestions(decrypted);
        const mappedFaqs: FaqQuestionItem[] = rows.map((item: any, index: number) => {
          const subQuestionsIdsRaw = item.sub_questions_ids || item.subQuestionsIds || [];
          const subQuestionsIds = Array.isArray(subQuestionsIdsRaw)
            ? subQuestionsIdsRaw.map((id: unknown) => String(id))
            : [];
          const subQuestionsCount = subQuestionsIds.length
            ? subQuestionsIds.length
            : Number(item.sub_questions_count || item.subQuestionsCount || 0);

          return {
            question_id: String(item.question_id || item._id || item.id || `faq-${index}`),
            category: item.category || '',
            question: item.question || '',
            answer: item.answer || '',
            keyword: item.keyword || item.keywords || '',
            url: item.url || '',
            url_label: item.url_label || item.urlLabel || '',
            image_url: item.image_url || item.imageUrl || '',
            question_type: item.question_type || item.questionType || 'static',
            intent: item.intent || item.webhook_name || '',
            sub_questions_ids: subQuestionsIds,
            sub_questions_count: Number.isFinite(subQuestionsCount) ? subQuestionsCount : 0,
            webhook_id: String(item.webhook_id || item.webhookId || ''),
            webhook_name: item.webhook_name || item.webhookName || item.intent || '',
          };
        });
        setFaqQuestions(mappedFaqs);
        setFaqCategories(extractFaqCategories(decrypted, mappedFaqs));
      } else {
        setFaqQuestions([]);
        setFaqCategories([]);
      }
    } catch (error) {
      console.log('Error fetching FAQ questions:', error);
      toast.error('Failed to load FAQ questions. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setFaqQuestions([]);
      setFaqCategories([]);
    } finally {
      setIsLoadingFaqQuestions(false);
    }
  };

  const fetchWebhooks = async (): Promise<WebhookItem[]> => {
    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId) {
      setWebhooks([]);
      return [];
    }

    setIsLoadingWebhooks(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/getApplicattionChatBots`,
        payload
      );

      if (response.data?.status === 200 && response.data.data) {
        const decrypted = await decryptResponse<any>(
          response.data.data,
          getSessionKey() as string
        );
        const pageBots = extractBotsFromDecrypted(decrypted);
        const currentBot = pageBots.find(
          (item: any) =>
            item.bot_id === botId ||
            item._id === botId ||
            item.id === botId
        );
        const apis = Array.isArray(currentBot?.apis) ? currentBot.apis : [];
        const mappedWebhooks = apis.map(mapWebhookItem);
        setWebhooks(mappedWebhooks);
        return mappedWebhooks;
      }

      setWebhooks([]);
      return [];
    } catch (error) {
      console.log('Error fetching webhooks:', error);
      toast.error('Failed to load webhooks. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setWebhooks([]);
      return [];
    } finally {
      setIsLoadingWebhooks(false);
    }
  };

  const resolveFaqWebhookSelection = (
    item: FaqQuestionItem,
    availableWebhooks: WebhookItem[],
    pendingIntent?: PendingIntentUpdate
  ): { webhook_id: string; webhook_name: string; hasIntent: boolean } => {
    const intentWebhookId = String(
      pendingIntent?.webhook_id || item.webhook_id || ''
    ).trim();
    const intentWebhookName = String(
      pendingIntent?.webhook_name || item.webhook_name || item.intent || ''
    ).trim();

    const matchedById = intentWebhookId
      ? availableWebhooks.find((webhook) => webhook.webhook_id === intentWebhookId)
      : undefined;
    const matchedByName = intentWebhookName
      ? availableWebhooks.find(
          (webhook) =>
            webhook.webhook_name.trim().toLowerCase() === intentWebhookName.toLowerCase()
        )
      : undefined;
    const matchedWebhook = matchedById || matchedByName;

    return {
      webhook_id: matchedWebhook?.webhook_id || intentWebhookId,
      webhook_name: matchedWebhook?.webhook_name || intentWebhookName,
      hasIntent: Boolean(matchedWebhook || intentWebhookId || intentWebhookName),
    };
  };

  useEffect(() => {
    setFaqListPage(0);
    setSelectedFaqIds([]);
    setEditingWebhookId(null);
    setWebhookForm(emptyWebhookForm());
    setWebhookToDelete(null);
    setWebhookActionAnchor(null);
    setWebhookActionTarget(null);
    setWebhookTestDialogOpen(false);
    setWebhookUnderTest(null);
    setWebhookTestPlaceholders([]);
    setWebhookTestValues({});
    setWebhookTestResult('');
    setFaqFormDialogOpen(false);
    setEditingFaqQuestionId(null);
    setFaqForm(emptyFaqForm());
    setIsAddingNewCategory(false);
    setFaqCategories([]);
    setPendingSubQuestionUpdates({});
    setPendingIntentUpdates({});
    setFaqActionAnchor(null);
    setFaqActionTarget(null);
    fetchFaqQuestions();
    fetchWebhooks();
  }, [bot.bot_id, bot.id]);

  const hasFaqQuestions = faqQuestions.length > 0;
  const FAQ_PAGE_SIZE = 10;
  const totalFaqPages = Math.max(1, Math.ceil(faqs.length / FAQ_PAGE_SIZE));
  const previewStart = faqPreviewPage * FAQ_PAGE_SIZE;
  const previewFaqs = faqs.slice(previewStart, previewStart + FAQ_PAGE_SIZE);
  const previewEnd = Math.min(previewStart + FAQ_PAGE_SIZE, faqs.length);
  const totalFaqListPages = Math.max(1, Math.ceil(faqQuestions.length / FAQ_PAGE_SIZE));
  const listStart = faqListPage * FAQ_PAGE_SIZE;
  const pagedFaqQuestions = faqQuestions.slice(listStart, listStart + FAQ_PAGE_SIZE);
  const listEnd = Math.min(listStart + FAQ_PAGE_SIZE, faqQuestions.length);
  const hasPendingFaqUpdates =
    Object.keys(pendingSubQuestionUpdates).length > 0 ||
    Object.keys(pendingIntentUpdates).length > 0;

  const getDisplayIntent = (item: FaqQuestionItem): string => {
    const pending = pendingIntentUpdates[item.question_id];
    if (pending) return pending.webhook_name || pending.webhook_id;
    return item.webhook_name || item.intent || '-';
  };

  const getDisplaySubQuestionsCount = (item: FaqQuestionItem): number => {
    const pending = pendingSubQuestionUpdates[item.question_id];
    if (pending) return pending.length;
    return item.sub_questions_ids.length || item.sub_questions_count;
  };

  useEffect(() => {
    if (!faqFormDialogOpen || webhooks.length === 0) return;

    setFaqForm((prev) => {
      if (!prev.webhook_id && !prev.webhook_name) return prev;

      const matchedWebhook =
        webhooks.find((webhook) => webhook.webhook_id === prev.webhook_id) ||
        webhooks.find(
          (webhook) =>
            prev.webhook_name &&
            webhook.webhook_name.trim().toLowerCase() === prev.webhook_name.trim().toLowerCase()
        );

      if (!matchedWebhook) return prev;
      if (
        matchedWebhook.webhook_id === prev.webhook_id &&
        matchedWebhook.webhook_name === prev.webhook_name &&
        prev.question_type === 'dynamic'
      ) {
        return prev;
      }

      return {
        ...prev,
        question_type: 'dynamic',
        webhook_id: matchedWebhook.webhook_id,
        webhook_name: matchedWebhook.webhook_name,
      };
    });
  }, [faqFormDialogOpen, webhooks]);

  useEffect(() => {
    setFaqListPage((page) => Math.min(page, Math.max(0, totalFaqListPages - 1)));
  }, [totalFaqListPages]);

  useEffect(() => {
    const validIds = new Set(faqQuestions.map((item) => item.question_id));
    setSelectedFaqIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [faqQuestions]);

  const handleActivateChatBot = async (): Promise<void> => {
    if (!hasFaqQuestions) {
      toast.error('Add FAQ questions before activating this chat bot.', {
        position: 'top-right',
      });
      return;
    }

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId) {
      toast.error('Organization, application, or bot details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    setIsActivating(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_id: botId,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/activateChatBot`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(response.data.message || 'Chat bot activated successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
      } else {
        toast.error(response.data?.message || 'Failed to activate chat bot. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while activating the chat bot. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsActivating(false);
    }
  };

  const handleDeleteFaqQuestion = async (): Promise<void> => {
    if (!faqToDelete) return;

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId || !faqToDelete.question_id) {
      toast.error('Organization, application, bot, or question details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    setIsDeletingFaq(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_id: botId,
          question_id: faqToDelete.question_id,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/deleteChatBotFaq`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(response.data.message || 'FAQ question deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        setFaqToDelete(null);
        await fetchFaqQuestions();
      } else {
        toast.error(response.data?.message || 'Failed to delete FAQ question. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while deleting the FAQ question. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsDeletingFaq(false);
    }
  };

  const handleToggleFaqSelection = (questionId: string): void => {
    setSelectedFaqIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleToggleSelectAllVisibleFaqs = (): void => {
    const visibleIds = pagedFaqQuestions.map((item) => item.question_id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedFaqIds.includes(id));

    if (allVisibleSelected) {
      setSelectedFaqIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    setSelectedFaqIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const handleDeleteSelectedFaqs = async (): Promise<void> => {
    if (selectedFaqIds.length === 0) return;

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId) {
      toast.error('Organization, application, or bot details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    setIsDeletingSelectedFaqs(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_id: botId,
          question_ids: selectedFaqIds,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/deleteChatBotFaqs`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(
          response.data.message ||
            `${selectedFaqIds.length} FAQ question${selectedFaqIds.length === 1 ? '' : 's'} deleted successfully!`,
          {
            position: 'top-right',
            autoClose: 3000,
          }
        );
        setBulkDeleteDialogOpen(false);
        setSelectedFaqIds([]);
        await fetchFaqQuestions();
      } else {
        toast.error(
          response.data?.message || 'Failed to delete selected FAQ questions. Please try again.',
          {
            position: 'top-right',
            autoClose: 3000,
          }
        );
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while deleting the selected FAQ questions. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsDeletingSelectedFaqs(false);
    }
  };

  const handleOpenAddFaqForm = (): void => {
    setEditingFaqQuestionId(null);
    setFaqForm(emptyFaqForm());
    setIsAddingNewCategory(false);
    if (webhooks.length === 0) {
      fetchWebhooks();
    }
    setFaqFormDialogOpen(true);
  };

  const handleOpenEditFaqForm = async (item: FaqQuestionItem): Promise<void> => {
    const pendingIntent = pendingIntentUpdates[item.question_id];
    const initialIntentId = String(pendingIntent?.webhook_id || item.webhook_id || '').trim();
    const initialIntentName = String(
      pendingIntent?.webhook_name || item.webhook_name || item.intent || ''
    ).trim();
    const hasIntent = Boolean(initialIntentId || initialIntentName);
    const existingCategory = String(item.category || '').trim();
    const categoryExists = faqCategories.some(
      (category) => category.toLowerCase() === existingCategory.toLowerCase()
    );

    setEditingFaqQuestionId(item.question_id);
    setIsAddingNewCategory(Boolean(existingCategory) && !categoryExists);
    setFaqForm({
      category: existingCategory,
      question: item.question || '',
      answer: item.answer || '',
      keyword: item.keyword || '',
      url: item.url || '',
      url_label: item.url_label || '',
      image_url: item.image_url || '',
      question_type: item.question_type === 'dynamic' || hasIntent ? 'dynamic' : 'static',
      webhook_id: initialIntentId,
      webhook_name: initialIntentName,
      sub_questions_ids: [...(item.sub_questions_ids || [])],
    });
    setFaqFormDialogOpen(true);

    const availableWebhooks = webhooks.length > 0 ? webhooks : await fetchWebhooks();
    const resolvedWebhook = resolveFaqWebhookSelection(item, availableWebhooks, pendingIntent);

    setFaqForm((prev) => ({
      ...prev,
      question_type:
        prev.question_type === 'dynamic' || resolvedWebhook.hasIntent ? 'dynamic' : 'static',
      webhook_id: resolvedWebhook.webhook_id || prev.webhook_id,
      webhook_name: resolvedWebhook.webhook_name || prev.webhook_name,
    }));
  };

  const handleFaqCategorySelectChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const value = event.target.value;
    if (value === ADD_NEW_CATEGORY_VALUE) {
      setIsAddingNewCategory(true);
      setFaqForm((prev) => ({ ...prev, category: '' }));
      return;
    }
    setIsAddingNewCategory(false);
    setFaqForm((prev) => ({ ...prev, category: value }));
  };

  const handleFaqFormFieldChange = (field: keyof FaqFormState) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFaqForm((prev) => {
      if (field === 'question_type') {
        const nextType = value === 'dynamic' ? 'dynamic' : 'static';
        return {
          ...prev,
          question_type: nextType,
          webhook_id: nextType === 'static' ? '' : prev.webhook_id,
          webhook_name: nextType === 'static' ? '' : prev.webhook_name,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleFaqFormWebhookChange = (webhookId: string): void => {
    const selectedWebhook = webhooks.find((item) => item.webhook_id === webhookId);
    setFaqForm((prev) => ({
      ...prev,
      webhook_id: webhookId,
      webhook_name: selectedWebhook?.webhook_name || '',
    }));
  };

  const handleToggleFaqFormSubQuestion = (questionId: string): void => {
    setFaqForm((prev) => ({
      ...prev,
      sub_questions_ids: prev.sub_questions_ids.includes(questionId)
        ? prev.sub_questions_ids.filter((id) => id !== questionId)
        : [...prev.sub_questions_ids, questionId],
    }));
  };

  const handleSaveFaqForm = async (): Promise<void> => {
    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;
    const category = faqForm.category.trim();
    const question = faqForm.question.trim();
    const answer = faqForm.answer.trim();
    const keyword = faqForm.keyword.trim();

    if (!organizationId || !applicationId || !botId) {
      toast.error('Organization, application, or bot details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    if (!category || !question || !keyword) {
      toast.error('Category, Question, and Keyword are required.', {
        position: 'top-right',
      });
      return;
    }

    if (faqForm.question_type === 'static' && !answer) {
      toast.error('Answer is required for static questions.', {
        position: 'top-right',
      });
      return;
    }

    if (faqForm.question_type === 'dynamic') {
      if (!faqForm.webhook_id || !faqForm.webhook_name) {
        toast.error('Webhook and webhook name are required for dynamic questions.', {
          position: 'top-right',
        });
        return;
      }
    }

    setIsSavingFaqForm(true);
    try {
      const isEdit = Boolean(editingFaqQuestionId);
      const payloadData = {
        organization_id: organizationId,
        application_id: applicationId,
        bot_id: botId,
        ...(isEdit ? { question_id: editingFaqQuestionId } : {}),
        category,
        question,
        answer,
        keyword,
        url: faqForm.url.trim(),
        url_label: faqForm.url_label.trim(),
        image_url: faqForm.image_url.trim(),
        question_type: faqForm.question_type,
        webhook_id: faqForm.webhook_id || '',
        webhook_name: faqForm.webhook_name || '',
        sub_questions_ids: faqForm.sub_questions_ids,
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/${isEdit ? 'editChatBotFaq' : 'addChatBotFaq'}`,
        {
          data: await encryptResponse(payloadData, REQ_ENC_KEY),
        }
      );

      if (response.data?.status === 200) {
        toast.success(
          response.data.message ||
            (isEdit ? 'FAQ question updated successfully!' : 'FAQ question added successfully!'),
          {
            position: 'top-right',
            autoClose: 3000,
          }
        );
        setFaqCategories((prev) => {
          if (!category) return prev;
          if (prev.some((item) => item.toLowerCase() === category.toLowerCase())) return prev;
          return [...prev, category].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
          );
        });
        setFaqFormDialogOpen(false);
        setEditingFaqQuestionId(null);
        setFaqForm(emptyFaqForm());
        setIsAddingNewCategory(false);
        await fetchFaqQuestions();
      } else {
        toast.error(
          response.data?.message ||
            `Failed to ${isEdit ? 'update' : 'add'} FAQ question. Please try again.`,
          {
            position: 'top-right',
            autoClose: 3000,
          }
        );
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        `An error occurred while ${editingFaqQuestionId ? 'updating' : 'adding'} the FAQ question. Please try again.`;
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSavingFaqForm(false);
    }
  };

  const handleFaqActionMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    item: FaqQuestionItem
  ): void => {
    setFaqActionAnchor(event.currentTarget);
    setFaqActionTarget(item);
  };

  const handleFaqActionMenuClose = (): void => {
    setFaqActionAnchor(null);
  };

  const handleOpenAddSubQuestions = (): void => {
    if (!faqActionTarget) return;
    const existing =
      pendingSubQuestionUpdates[faqActionTarget.question_id] ||
      faqActionTarget.sub_questions_ids ||
      [];
    setDraftSubQuestionIds([...existing]);
    setSubQuestionSearch('');
    setSubQuestionsDialogOpen(true);
    handleFaqActionMenuClose();
  };

  const handleOpenAddIntent = (): void => {
    if (!faqActionTarget) return;
    const pending = pendingIntentUpdates[faqActionTarget.question_id];
    setDraftWebhookId(pending?.webhook_id || faqActionTarget.webhook_id || '');
    if (webhooks.length === 0) {
      fetchWebhooks();
    }
    setIntentDialogOpen(true);
    handleFaqActionMenuClose();
  };

  const handleDeleteFromFaqMenu = (): void => {
    if (!faqActionTarget) return;
    setFaqToDelete(faqActionTarget);
    handleFaqActionMenuClose();
  };

  const handleSaveDraftSubQuestions = (): void => {
    if (!faqActionTarget) return;
    setPendingSubQuestionUpdates((prev) => ({
      ...prev,
      [faqActionTarget.question_id]: [...draftSubQuestionIds],
    }));
    setSubQuestionsDialogOpen(false);
    toast.success('Sub questions added. Click Submit to save changes.', {
      position: 'top-right',
      autoClose: 2500,
    });
  };

  const handleSaveDraftIntent = (): void => {
    if (!faqActionTarget) return;
    const selectedWebhook = webhooks.find((item) => item.webhook_id === draftWebhookId);
    if (!selectedWebhook) {
      toast.error('Please select a webhook for intent.', { position: 'top-right' });
      return;
    }
    setPendingIntentUpdates((prev) => ({
      ...prev,
      [faqActionTarget.question_id]: {
        webhook_id: selectedWebhook.webhook_id,
        webhook_name: selectedWebhook.webhook_name,
      },
    }));
    setIntentDialogOpen(false);
    toast.success('Intent added. Click Submit to save changes.', {
      position: 'top-right',
      autoClose: 2500,
    });
  };

  const handleToggleDraftSubQuestion = (questionId: string): void => {
    setDraftSubQuestionIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleSubmitFaqUpdates = async (): Promise<void> => {
    if (!hasPendingFaqUpdates) return;

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId) {
      toast.error('Organization, application, or bot details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    const subQuestionsPayload = Object.entries(pendingSubQuestionUpdates).map(
      ([question_id, sub_questions_ids]) => ({
        question_id,
        sub_questions_ids,
      })
    );
    const intentPayload = Object.entries(pendingIntentUpdates).map(
      ([question_id, intent]) => ({
        question_id,
        webhook_id: intent.webhook_id,
        webhook_name: intent.webhook_name,
      })
    );

    setIsSubmittingFaqUpdates(true);
    try {
      let allSucceeded = true;

      if (subQuestionsPayload.length > 0) {
        const response = await httpService.post<UpdateChatBotResponse>(
          `${API_BASE_URL}/user/updateFaqSubQuestions`,
          {
            data: await encryptResponse({
              organization_id: organizationId,
              application_id: applicationId,
              bot_id: botId,
              questions: subQuestionsPayload,
            }, REQ_ENC_KEY),
          }
        );

        if (response.data?.status !== 200) {
          allSucceeded = false;
          toast.error(
            response.data?.message || 'Failed to update FAQ sub questions. Please try again.',
            { position: 'top-right', autoClose: 3000 }
          );
        }
      }

      if (intentPayload.length > 0) {
        const response = await httpService.post<UpdateChatBotResponse>(
          `${API_BASE_URL}/user/updateFaqWebhook`,
          {
            data: await encryptResponse({
              organization_id: organizationId,
              application_id: applicationId,
              bot_id: botId,
              questions: intentPayload,
            }, REQ_ENC_KEY),
          }
        );

        if (response.data?.status !== 200) {
          allSucceeded = false;
          toast.error(
            response.data?.message || 'Failed to update FAQ intents. Please try again.',
            { position: 'top-right', autoClose: 3000 }
          );
        }
      }

      if (allSucceeded) {
        toast.success('FAQ questions updated successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        setPendingSubQuestionUpdates({});
        setPendingIntentUpdates({});
        await fetchFaqQuestions();
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while updating FAQ questions. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSubmittingFaqUpdates(false);
    }
  };

  const syncWebhooksFromResponse = async (encryptedData: any): Promise<void> => {
    if (!encryptedData) {
      await fetchWebhooks();
      return;
    }
    try {
      const decrypted = await decryptResponse<any>(encryptedData, getSessionKey() as string);
      const apis = extractApisFromDecrypted(decrypted);
      if (apis.length > 0 || decrypted?.chatBot || decrypted?.chat_bot) {
        setWebhooks(apis.map(mapWebhookItem));
        return;
      }
      await fetchWebhooks();
    } catch {
      await fetchWebhooks();
    }
  };

  const handleOpenCreateWebhook = (): void => {
    setEditingWebhookId(null);
    setWebhookForm(emptyWebhookForm());
    setActiveSection('webhook-form');
  };

  const handleOpenEditWebhook = (webhook: WebhookItem): void => {
    setEditingWebhookId(webhook.webhook_id);
    setWebhookForm({
      webhook_name: webhook.webhook_name,
      method: WEBHOOK_METHODS.includes(webhook.method as typeof WEBHOOK_METHODS[number])
        ? webhook.method
        : 'POST',
      url: webhook.url,
      headersText: toPrettyJson(webhook.headers),
      bodyText: toPrettyJson(webhook.body),
      responseMapText: toPrettyJson(webhook.response_map),
    });
    setActiveSection('webhook-form');
  };

  const handleWebhookActionMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    webhook: WebhookItem
  ): void => {
    setWebhookActionAnchor(event.currentTarget);
    setWebhookActionTarget(webhook);
  };

  const handleWebhookActionMenuClose = (): void => {
    setWebhookActionAnchor(null);
  };

  const handleEditFromWebhookMenu = (): void => {
    if (!webhookActionTarget) return;
    handleOpenEditWebhook(webhookActionTarget);
    handleWebhookActionMenuClose();
  };

  const handleDeleteFromWebhookMenu = (): void => {
    if (!webhookActionTarget) return;
    setWebhookToDelete(webhookActionTarget);
    handleWebhookActionMenuClose();
  };

  const handleOpenTestWebhook = (webhook: WebhookItem): void => {
    const placeholders = extractWebhookTestPlaceholders(webhook);
    const initialValues: Record<string, string> = {};
    placeholders.forEach((key) => {
      initialValues[key] = '';
    });
    setWebhookUnderTest(webhook);
    setWebhookTestPlaceholders(placeholders);
    setWebhookTestValues(initialValues);
    setWebhookTestResult('');
    setWebhookTestDialogOpen(true);
  };

  const handleWebhookTestValueChange = (placeholderKey: string, value: string): void => {
    setWebhookTestValues((prev) => ({ ...prev, [placeholderKey]: value }));
  };

  const handleUpdateWebhookStatus = async (
    webhook: WebhookItem,
    webhookStatus: 'Active' | 'InActive'
  ): Promise<void> => {
    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId || !webhook.webhook_id) {
      return;
    }

    setIsUpdatingWebhookStatus(true);
    try {
      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/updateWebhookStatus`,
        {
          data: await encryptResponse({
            organization_id: organizationId,
            application_id: applicationId,
            bot_id: botId,
            webhook_id: webhook.webhook_id,
            webhook_status: webhookStatus,
          }, REQ_ENC_KEY),
        }
      );

      if (response.data?.status === 200) {
        await syncWebhooksFromResponse(response.data.data);
        setWebhooks((prev) => {
          const hasWebhook = prev.some((item) => item.webhook_id === webhook.webhook_id);
          if (!hasWebhook) return prev;
          return prev.map((item) =>
            item.webhook_id === webhook.webhook_id
              ? { ...item, status: webhookStatus }
              : item
          );
        });
      } else {
        toast.error(
          response.data?.message || 'Failed to update webhook status. Please try again.',
          { position: 'top-right', autoClose: 3000 }
        );
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while updating webhook status. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsUpdatingWebhookStatus(false);
    }
  };

  const handleRunWebhookTest = async (): Promise<void> => {
    if (!webhookUnderTest) return;

    const missingField = webhookTestPlaceholders.find(
      (key) => !String(webhookTestValues[key] || '').trim()
    );
    if (missingField) {
      toast.error(
        `Please fill ${getWebhookPlaceholderFieldName(missingField)} before testing.`,
        { position: 'top-right' }
      );
      return;
    }

    const method = String(webhookUnderTest.method || 'GET').toUpperCase();
    const resolvedUrl = String(
      replaceWebhookPlaceholders(webhookUnderTest.url, webhookTestValues) || ''
    ).trim();
    if (!resolvedUrl) {
      toast.error('Webhook URL is missing.', { position: 'top-right' });
      return;
    }

    const resolvedHeadersRaw = replaceWebhookPlaceholders(
      webhookUnderTest.headers || {},
      webhookTestValues
    );
    const resolvedHeaders: Record<string, string> = {};
    if (resolvedHeadersRaw && typeof resolvedHeadersRaw === 'object' && !Array.isArray(resolvedHeadersRaw)) {
      Object.entries(resolvedHeadersRaw as Record<string, unknown>).forEach(([key, value]) => {
        resolvedHeaders[key] = String(value ?? '');
      });
    }

    const resolvedBody =
      method === 'GET'
        ? undefined
        : replaceWebhookPlaceholders(webhookUnderTest.body || {}, webhookTestValues);

    setIsTestingWebhook(true);
    setWebhookTestResult('');
    try {
      const response = await axios({
        method: method.toLowerCase(),
        url: resolvedUrl,
        headers: resolvedHeaders,
        data: resolvedBody,
        validateStatus: () => true,
      });

      const resultPayload = {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
      };
      setWebhookTestResult(JSON.stringify(resultPayload, null, 2));

      const isSuccess = response.status >= 200 && response.status < 300;
      await handleUpdateWebhookStatus(webhookUnderTest, isSuccess ? 'Active' : 'InActive');

      if (isSuccess) {
        toast.success('Webhook test successful! Status set to Active.', {
          position: 'top-right',
          autoClose: 3000,
        });
      } else {
        toast.error(`Webhook test failed with status ${response.status}. Status set to InActive.`, {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      const message =
        error?.message || 'An error occurred while testing the webhook. Please try again.';
      setWebhookTestResult(
        JSON.stringify(
          {
            success: false,
            message,
          },
          null,
          2
        )
      );
      await handleUpdateWebhookStatus(webhookUnderTest, 'InActive');
      toast.error(`${message} Status set to InActive.`, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsTestingWebhook(false);
    }
  };

  const handleWebhookFormChange = (field: keyof WebhookFormState) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setWebhookForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveWebhook = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;
    const webhookName = webhookForm.webhook_name.trim();
    const webhookUrl = webhookForm.url.trim();
    const method = String(webhookForm.method || '').toUpperCase();

    if (!organizationId || !applicationId || !botId) {
      toast.error('Organization, application, or bot details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    if (!webhookName || !method || !webhookUrl) {
      toast.error('Webhook name, method, and URL are required.', { position: 'top-right' });
      return;
    }

    if (!WEBHOOK_METHODS.includes(method as typeof WEBHOOK_METHODS[number])) {
      toast.error('Invalid webhook method.', { position: 'top-right' });
      return;
    }

    const headers = parseJsonObject(webhookForm.headersText, 'Headers');
    if (headers === null) return;
    const body =
      method === 'GET'
        ? {}
        : parseJsonObject(webhookForm.bodyText, 'Body');
    if (body === null) return;
    const responseMap = parseJsonObject(webhookForm.responseMapText, 'Response map');
    if (responseMap === null) return;

    setIsSavingWebhook(true);
    try {
      const isEdit = Boolean(editingWebhookId);
      const payloadData = isEdit
        ? {
            organization_id: organizationId,
            application_id: applicationId,
            bot_id: botId,
            webhook_id: editingWebhookId,
            webhook_name: webhookName,
            method,
            url: webhookUrl,
            headers,
            body,
            response_map: responseMap,
          }
        : {
            organization_id: organizationId,
            application_id: applicationId,
            bot_id: botId,
            webhook_name: webhookName,
            method,
            url: webhookUrl,
            headers,
            body,
            response_map: responseMap,
          };

      const payload = {
        data: await encryptResponse(payloadData, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/${isEdit ? 'editWebhook' : 'createWebhook'}`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(
          response.data.message ||
            (isEdit ? 'Webhook updated successfully!' : 'Webhook created successfully!'),
          {
            position: 'top-right',
            autoClose: 3000,
          }
        );
        await syncWebhooksFromResponse(response.data.data);
        setEditingWebhookId(null);
        setWebhookForm(emptyWebhookForm());
        setActiveSection('webhooks');
      } else {
        toast.error(
          response.data?.message ||
            `Failed to ${isEdit ? 'update' : 'create'} webhook. Please try again.`,
          {
            position: 'top-right',
            autoClose: 3000,
          }
        );
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        `An error occurred while ${editingWebhookId ? 'updating' : 'creating'} the webhook. Please try again.`;
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSavingWebhook(false);
    }
  };

  const handleDeleteWebhook = async (): Promise<void> => {
    if (!webhookToDelete) return;

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId || !webhookToDelete.webhook_id) {
      toast.error('Organization, application, bot, or webhook details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    setIsDeletingWebhook(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_id: botId,
          webhook_id: webhookToDelete.webhook_id,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/deleteWebhook`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(response.data.message || 'Webhook deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        setWebhookToDelete(null);
        await syncWebhooksFromResponse(response.data.data);
      } else {
        toast.error(response.data?.message || 'Failed to delete webhook. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while deleting the webhook. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsDeletingWebhook(false);
    }
  };

  const handleUpdateDetails = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      toast.error('Please enter a chat bot name.', { position: 'top-right' });
      return;
    }

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId) {
      toast.error('Organization, application, or bot details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_id: botId,
          bot_name: details.bot_name.trim(),
          welcome_message: details.welcome_message.trim(),
          no_result_message: details.no_result_message.trim(),
          handover_message: details.handover_message.trim(),
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/updateChatBot`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(response.data.message || 'Chat bot updated successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        onUpdated();
      } else {
        toast.error(response.data?.message || 'Failed to update chat bot. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while updating the chat bot. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteChatBot = async (): Promise<void> => {
    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId) {
      toast.error('Organization, application, or bot details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    setIsDeleting(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_id: botId,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/deleteChatBot`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(response.data.message || 'Chat bot deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        setDeleteDialogOpen(false);
        onUpdated();
      } else {
        toast.error(response.data?.message || 'Failed to delete chat bot. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while deleting the chat bot. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFaqFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a valid CSV file', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setFaqFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const allRows = (results.data as Record<string, unknown>[]).map(mapCsvRowToFaq);
        const parsedFaqs = allRows.filter(
          (row) => row.category && row.question && row.answer && row.keyword
        );
        const skippedCount = allRows.length - parsedFaqs.length;

        if (parsedFaqs.length === 0) {
          setFaqs([]);
          setFaqPreviewPage(0);
          toast.error(
            'No valid FAQs found. Category, Question, Answer, and Keyword are required.',
            {
              position: 'top-right',
              autoClose: 5000,
            }
          );
          return;
        }

        setFaqs(parsedFaqs);
        setFaqPreviewPage(0);
        if (skippedCount > 0) {
          toast.warning(
            `${parsedFaqs.length} FAQ(s) loaded. ${skippedCount} row(s) skipped because required fields were missing.`,
            {
              position: 'top-right',
              autoClose: 4000,
            }
          );
        } else {
          toast.success(`${parsedFaqs.length} FAQ(s) loaded from CSV.`, {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      },
      error: () => {
        setFaqs([]);
        setFaqPreviewPage(0);
        toast.error('Failed to parse CSV file. Please check the file format.', {
          position: 'top-right',
          autoClose: 3000,
        });
      },
    });
  };

  const handleDownloadSampleCsv = (): void => {
    const csvContent = Papa.unparse({
      fields: ['Category', 'Question', 'Answer', 'Keyword', 'URL', 'URL Label', 'Image URL'],
      data: [
        [
          'General',
          'What are your working hours?',
          'We are available 24/7 to help you.',
          'hours, timing, availability',
          'https://example.com/hours',
          'Working Hours',
          'https://example.com/hours.png',
        ],
        [
          'Support',
          'How can I contact support?',
          'You can chat with us here or email support.',
          'support, contact, help',
          'https://example.com/support',
          'Contact Support',
          '',
        ],
      ],
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'chatbot_faq_sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleUploadFaqs = async (): Promise<void> => {
    if (faqs.length === 0) {
      toast.error('Please upload a CSV file with FAQ records first.', {
        position: 'top-right',
      });
      return;
    }

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';
    const botId = bot.bot_id || bot.id;

    if (!organizationId || !applicationId || !botId) {
      toast.error('Organization, application, or bot details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    setIsUploadingFaq(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_id: botId,
          faqs,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<UpdateChatBotResponse>(
        `${API_BASE_URL}/user/uploadChatBotFaqs`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(response.data.message || 'FAQs uploaded successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        setFaqs([]);
        setFaqPreviewPage(0);
        setFaqFileName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await fetchFaqQuestions();
      } else {
        toast.error(response.data?.message || 'Failed to upload FAQs. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while uploading FAQs. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsUploadingFaq(false);
    }
  };

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <PageHeader
        heading="Update Chat Bot"
        subheading="Update bot replies, manage FAQ answers, and configure webhooks."
        variant="gradient"
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <ToggleButtonGroup
          exclusive
          value={activeSection}
          onChange={handleSectionChange}
          sx={{
            backgroundColor: '#fff',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0, 122, 255, 0.08)',
            flexWrap: 'wrap',
          }}
        >
          <ToggleButton
            value="details"
            sx={{
              px: 3,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                background: themeBackgrounds.default,
                color: '#ffffff',
                '&:hover': {
                  background: themeBackgrounds.default,
                  opacity: 0.9,
                },
              },
            }}
          >
            <EditIcon sx={{ mr: 1, fontSize: 18 }} />
            Update Bot Details
          </ToggleButton>
          <ToggleButton
            value="faq"
            sx={{
              px: 3,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                background: themeBackgrounds.default,
                color: '#ffffff',
                '&:hover': {
                  background: themeBackgrounds.default,
                  opacity: 0.9,
                },
              },
            }}
          >
            <UploadFileIcon sx={{ mr: 1, fontSize: 18 }} />
            Upload FAQs
          </ToggleButton>
          <ToggleButton
            value="faq-list"
            sx={{
              px: 3,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                background: themeBackgrounds.default,
                color: '#ffffff',
                '&:hover': {
                  background: themeBackgrounds.default,
                  opacity: 0.9,
                },
              },
            }}
          >
            <QuestionAnswerIcon sx={{ mr: 1, fontSize: 18 }} />
            FAQs List
          </ToggleButton>
          <ToggleButton
            value="webhooks"
            sx={{
              px: 3,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                background: themeBackgrounds.default,
                color: '#ffffff',
                '&:hover': {
                  background: themeBackgrounds.default,
                  opacity: 0.9,
                },
              },
            }}
          >
            <ListAltIcon sx={{ mr: 1, fontSize: 18 }} />
            Webhooks
          </ToggleButton>
          <ToggleButton
            value="webhook-form"
            sx={{
              px: 3,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                background: themeBackgrounds.default,
                color: '#ffffff',
                '&:hover': {
                  background: themeBackgrounds.default,
                  opacity: 0.9,
                },
              },
            }}
          >
            <HttpIcon sx={{ mr: 1, fontSize: 18 }} />
            {editingWebhookId ? 'Edit Webhook' : 'Add Webhook'}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {activeSection === 'details' && (
        <Card>
          <CardContent>
            <Box component="form" onSubmit={handleUpdateDetails} noValidate>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Chat Bot Details
              </Typography>

              <TextField
                label="Chat bot name"
                placeholder="Enter chat bot name"
                fullWidth
                required
                value={details.bot_name}
                onChange={handleChange('bot_name')}
                disabled={isUpdating}
                sx={{ mb: 3 }}
              />

              <TextField
                label="Welcome message"
                fullWidth
                multiline
                minRows={2}
                value={details.welcome_message}
                onChange={handleChange('welcome_message')}
                disabled={isUpdating}
                sx={{ mb: 3 }}
              />

              <TextField
                label="No result message"
                fullWidth
                multiline
                minRows={2}
                value={details.no_result_message}
                onChange={handleChange('no_result_message')}
                disabled={isUpdating}
                sx={{ mb: 3 }}
              />

              <TextField
                label="Handover message"
                fullWidth
                multiline
                minRows={2}
                value={details.handover_message}
                onChange={handleChange('handover_message')}
                disabled={isUpdating}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isUpdating || isDeleting || isActivating}
                  >
                    Delete
                  </Button>
                  <Tooltip
                    title={hasFaqQuestions ? 'Activate this chat bot' : 'Add FAQ questions before activating this bot'}
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={isActivating ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                        onClick={handleActivateChatBot}
                        disabled={!hasFaqQuestions || isUpdating || isDeleting || isActivating}
                        sx={{
                          background: (!hasFaqQuestions || isUpdating || isDeleting || isActivating)
                            ? 'grey'
                            : themeBackgrounds.default,
                          color: '#ffffff',
                          '&:hover': {
                            background: themeBackgrounds.default,
                            opacity: 0.9,
                          },
                        }}
                      >
                        {isActivating ? 'Activating...' : 'Activate'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={onCancel} disabled={isUpdating || isDeleting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!isValid || isUpdating || isDeleting}
                    startIcon={isUpdating ? <CircularProgress size={18} color="inherit" /> : undefined}
                    sx={{
                      background: (!isValid || isUpdating || isDeleting) ? 'grey' : themeBackgrounds.default,
                      color: '#ffffff',
                      '&:hover': {
                        background: themeBackgrounds.default,
                        opacity: 0.9,
                      },
                    }}
                  >
                    {isUpdating ? 'Updating...' : 'Update'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeSection === 'faq' && (
        <Card>
          <CardContent>
            {/* <Typography variant="h6" sx={{ mb: 1 }}>
              Upload FAQs
            </Typography> */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Download the sample CSV, fill in your FAQs, then upload it. Category, Question, Answer, and Keyword are required. URL, URL Label, and Image URL are optional.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 4,
                mb: 3,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderColor: '#90caf9',
                backgroundColor: '#f8fbff',
              }}
            >
              <UploadFileIcon sx={{ fontSize: 42, color: '#007aff', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {faqFileName || 'Choose a CSV file'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Columns: Category, Question, Answer, Keyword, URL, URL Label, Image URL
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingFaq}
                  sx={{
                    background: themeBackgrounds.default,
                    color: '#ffffff',
                    '&:hover': {
                      background: themeBackgrounds.default,
                      opacity: 0.9,
                    },
                  }}
                >
                  Select CSV
                </Button>
                <Button variant="outlined" onClick={handleDownloadSampleCsv} disabled={isUploadingFaq}>
                  Download Sample
                </Button>
              </Box>
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFaqFileChange}
              />
            </Paper>

            {faqs.length > 0 && (
              <Box sx={{ mb: 3, overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Preview ({faqs.length} FAQ{faqs.length === 1 ? '' : 's'})
                  </Typography>
                  {totalFaqPages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label="Previous FAQ page"
                        disabled={faqPreviewPage === 0}
                        onClick={() => setFaqPreviewPage((page) => Math.max(0, page - 1))}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72, textAlign: 'center' }}>
                        {faqPreviewPage + 1} / {totalFaqPages}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Next FAQ page"
                        disabled={faqPreviewPage >= totalFaqPages - 1}
                        onClick={() =>
                          setFaqPreviewPage((page) => Math.min(totalFaqPages - 1, page + 1))
                        }
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Answer</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Keyword</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>URL</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>URL Label</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Image URL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewFaqs.map((faq, index) => (
                      <TableRow key={`${faq.question}-${previewStart + index}`}>
                        <TableCell>{faq.category}</TableCell>
                        <TableCell>{faq.question}</TableCell>
                        <TableCell>{faq.answer}</TableCell>
                        <TableCell>{faq.keyword}</TableCell>
                        <TableCell>{faq.url || '-'}</TableCell>
                        <TableCell>{faq.url_label || '-'}</TableCell>
                        <TableCell>{faq.image_url || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing {previewStart + 1}-{previewEnd} of {faqs.length} records.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={onCancel} disabled={isUploadingFaq}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUploadFaqs}
                disabled={faqs.length === 0 || isUploadingFaq}
                startIcon={isUploadingFaq ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
                sx={{
                  background: (faqs.length === 0 || isUploadingFaq) ? 'grey' : themeBackgrounds.default,
                  color: '#ffffff',
                  '&:hover': {
                    background: themeBackgrounds.default,
                    opacity: 0.9,
                  },
                }}
              >
                {isUploadingFaq ? 'Uploading...' : 'Upload FAQs'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeSection === 'faq-list' && (
        <Card>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  FAQ Question List
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View FAQ questions. Click a row to edit, or use Add FAQ. Actions support sub questions,
                  intent, and delete. Rows with unsaved changes are highlighted.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddFaqForm}
                  disabled={isSavingFaqForm || isDeletingFaq || isDeletingSelectedFaqs || isSubmittingFaqUpdates}
                  sx={{
                    background: themeBackgrounds.default,
                    color: '#ffffff',
                    '&:hover': {
                      background: themeBackgrounds.default,
                      opacity: 0.9,
                    },
                  }}
                >
                  Add FAQ
                </Button>
                {hasPendingFaqUpdates && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={
                      isSubmittingFaqUpdates ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    onClick={handleSubmitFaqUpdates}
                    disabled={
                      isSubmittingFaqUpdates ||
                      isDeletingFaq ||
                      isDeletingSelectedFaqs ||
                      isActivating
                    }
                  >
                    {isSubmittingFaqUpdates ? 'Submitting...' : 'Submit'}
                  </Button>
                )}
                {selectedFaqIds.length > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={
                      isDeletingSelectedFaqs ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DeleteIcon />
                      )
                    }
                    onClick={() => setBulkDeleteDialogOpen(true)}
                    disabled={isDeletingSelectedFaqs || isDeletingFaq || isActivating || isSubmittingFaqUpdates}
                  >
                    Delete Question{selectedFaqIds.length === 1 ? '' : 's'} ({selectedFaqIds.length})
                  </Button>
                )}
                <Tooltip
                  title={hasFaqQuestions ? 'Activate this chat bot' : 'Add FAQ questions before activating this bot'}
                >
                  <span>
                    <Button
                      variant="contained"
                      startIcon={isActivating ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                      onClick={handleActivateChatBot}
                      disabled={
                        !hasFaqQuestions ||
                        isActivating ||
                        isDeletingFaq ||
                        isDeletingSelectedFaqs ||
                        isSubmittingFaqUpdates
                      }
                      sx={{
                        background:
                          (!hasFaqQuestions ||
                            isActivating ||
                            isDeletingFaq ||
                            isDeletingSelectedFaqs ||
                            isSubmittingFaqUpdates)
                            ? 'grey'
                            : themeBackgrounds.default,
                        color: '#ffffff',
                        '&:hover': {
                          background: themeBackgrounds.default,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {isActivating ? 'Activating...' : 'Activate'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {isLoadingFaqQuestions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading FAQ questions...</Typography>
              </Box>
            ) : faqQuestions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No FAQ questions found. Upload a CSV to add questions.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {faqQuestions.length} FAQ{faqQuestions.length === 1 ? '' : 's'}
                  </Typography>
                  {totalFaqListPages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label="Previous FAQ list page"
                        disabled={faqListPage === 0}
                        onClick={() => setFaqListPage((page) => Math.max(0, page - 1))}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72, textAlign: 'center' }}>
                        {faqListPage + 1} / {totalFaqListPages}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Next FAQ list page"
                        disabled={faqListPage >= totalFaqListPages - 1}
                        onClick={() =>
                          setFaqListPage((page) => Math.min(totalFaqListPages - 1, page + 1))
                        }
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            pagedFaqQuestions.some((item) => selectedFaqIds.includes(item.question_id)) &&
                            !pagedFaqQuestions.every((item) => selectedFaqIds.includes(item.question_id))
                          }
                          checked={
                            pagedFaqQuestions.length > 0 &&
                            pagedFaqQuestions.every((item) => selectedFaqIds.includes(item.question_id))
                          }
                          onChange={handleToggleSelectAllVisibleFaqs}
                          disabled={isDeletingSelectedFaqs || isDeletingFaq}
                          inputProps={{ 'aria-label': 'Select all visible FAQ questions' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Answer</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Question Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Intent</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Sub Questions</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedFaqQuestions.map((item) => {
                      const isSelected = selectedFaqIds.includes(item.question_id);
                      const hasPendingChanges = Boolean(
                        pendingIntentUpdates[item.question_id] ||
                          pendingSubQuestionUpdates[item.question_id]
                      );
                      return (
                        <TableRow
                          key={item.question_id}
                          selected={isSelected || hasPendingChanges}
                          hover
                          onClick={() => handleOpenEditFaqForm(item)}
                          sx={{
                            cursor: 'pointer',
                            ...(hasPendingChanges
                              ? {
                                  backgroundColor: 'rgba(0, 122, 255, 0.08)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 122, 255, 0.14)',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(0, 122, 255, 0.12)',
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(0, 122, 255, 0.18)',
                                  },
                                  boxShadow: 'inset 3px 0 0 #007aff',
                                }
                              : {}),
                          }}
                        >
                          <TableCell
                            padding="checkbox"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleToggleFaqSelection(item.question_id)}
                              disabled={isDeletingSelectedFaqs || isDeletingFaq}
                              inputProps={{ 'aria-label': `Select FAQ ${item.question || item.question_id}` }}
                            />
                          </TableCell>
                          <TableCell>{item.category || '-'}</TableCell>
                          <TableCell>{item.question || '-'}</TableCell>
                          <TableCell>{item.answer || '-'}</TableCell>
                          <TableCell>{item.question_type || '-'}</TableCell>
                          <TableCell>{getDisplayIntent(item)}</TableCell>
                          <TableCell>{getDisplaySubQuestionsCount(item)}</TableCell>
                          <TableCell
                            align="right"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              aria-label="FAQ actions"
                              onClick={(event) => handleFaqActionMenuOpen(event, item)}
                              disabled={isDeletingFaq || isDeletingSelectedFaqs || isSubmittingFaqUpdates}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing {listStart + 1}-{listEnd} of {faqQuestions.length} records.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {activeSection === 'webhooks' && (
        <Card>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Webhooks
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and manage API webhooks configured for this chat bot. Click a row to test a webhook.
                </Typography>
              </Box>
              {/* <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateWebhook}
                  sx={{
                    background: themeBackgrounds.default,
                    color: '#ffffff',
                    '&:hover': {
                      background: themeBackgrounds.default,
                      opacity: 0.9,
                    },
                  }}
                >
                  Add Webhook
                </Button>
              </Box> */}
            </Box>

            {isLoadingWebhooks ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading webhooks...</Typography>
              </Box>
            ) : webhooks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No webhooks found. Create one to connect external APIs with this chat bot.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>URL</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webhooks.map((webhook) => {
                      const isActive =
                        String(webhook.status || '').toLowerCase() === 'active';
                      return (
                        <TableRow
                          key={webhook.webhook_id}
                          hover
                          onClick={() => {
                            if (
                              isDeletingWebhook ||
                              isSavingWebhook ||
                              isTestingWebhook ||
                              isUpdatingWebhookStatus
                            ) {
                              return;
                            }
                            handleOpenTestWebhook(webhook);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{webhook.webhook_name || '-'}</TableCell>
                          <TableCell>{webhook.method || '-'}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 360,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={webhook.url}
                          >
                            {webhook.url || '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={isActive ? 'Active' : 'InActive'}
                              color={isActive ? 'success' : 'default'}
                              variant={isActive ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              aria-label="Webhook actions"
                              onClick={(event) => handleWebhookActionMenuOpen(event, webhook)}
                              disabled={isDeletingWebhook || isSavingWebhook}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {activeSection === 'webhook-form' && (
        <Card>
          <CardContent>
            <Box component="form" onSubmit={handleSaveWebhook} noValidate>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {editingWebhookId ? 'Edit Webhook' : 'Add Webhook'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure the webhook name, method, URL, headers, body, and response mapping.
              </Typography>

              <Grid2 container spacing={3} sx={{ mb: 3 }}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Webhook name"
                    placeholder="Enter webhook name"
                    fullWidth
                    required
                    value={webhookForm.webhook_name}
                    onChange={handleWebhookFormChange('webhook_name')}
                    disabled={isSavingWebhook}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    label="Method"
                    fullWidth
                    required
                    value={webhookForm.method}
                    onChange={handleWebhookFormChange('method')}
                    disabled={isSavingWebhook}
                    sx={{
                      '& .MuiSelect-select': {
                        textAlign: 'left !important',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      },
                    }}
                  >
                    {WEBHOOK_METHODS.map((method) => (
                      <MenuItem key={method} value={method} sx={{ justifyContent: 'flex-start' }}>
                        {method}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="URL"
                    placeholder="https://example.com/api/endpoint"
                    fullWidth
                    required
                    value={webhookForm.url}
                    onChange={handleWebhookFormChange('url')}
                    disabled={isSavingWebhook}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Headers (JSON object)"
                    fullWidth
                    multiline
                    minRows={4}
                    value={webhookForm.headersText}
                    onChange={handleWebhookFormChange('headersText')}
                    disabled={isSavingWebhook}
                    sx={{ fontFamily: 'monospace' }}
                    helperText='Example: { "Authorization": "Bearer token" }'
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Body (JSON object)"
                    fullWidth
                    multiline
                    minRows={4}
                    value={webhookForm.bodyText}
                    onChange={handleWebhookFormChange('bodyText')}
                    disabled={isSavingWebhook || webhookForm.method === 'GET'}
                    sx={{ fontFamily: 'monospace' }}
                    helperText={
                      webhookForm.method === 'GET'
                        ? 'Body is disabled for GET requests.'
                        : 'Example: { "query": "{{user_message}}" }'
                    }
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Response map (JSON object)"
                    fullWidth
                    multiline
                    minRows={4}
                    value={webhookForm.responseMapText}
                    onChange={handleWebhookFormChange('responseMapText')}
                    disabled={isSavingWebhook}
                    sx={{ fontFamily: 'monospace' }}
                    helperText='Example: { "answer": "data.result" }'
                  />
                </Grid2>
              </Grid2>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingWebhookId(null);
                    setWebhookForm(emptyWebhookForm());
                    setActiveSection('webhooks');
                  }}
                  disabled={isSavingWebhook}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSavingWebhook}
                  startIcon={
                    isSavingWebhook ? <CircularProgress size={18} color="inherit" /> : <HttpIcon />
                  }
                  sx={{
                    background: isSavingWebhook ? 'grey' : themeBackgrounds.default,
                    color: '#ffffff',
                    '&:hover': {
                      background: themeBackgrounds.default,
                      opacity: 0.9,
                    },
                  }}
                >
                  {isSavingWebhook
                    ? editingWebhookId
                      ? 'Updating...'
                      : 'Creating...'
                    : editingWebhookId
                      ? 'Update Webhook'
                      : 'Create Webhook'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Chat Bot</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{details.bot_name || bot.bot_name}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteChatBot}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={faqFormDialogOpen}
        onClose={() => !isSavingFaqForm && setFaqFormDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingFaqQuestionId ? 'Edit FAQ Question' : 'Add FAQ Question'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Category, Question, and Keyword are required. Answer is required for static questions.
            Webhook / Intent is required for dynamic questions.
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label="Category"
                fullWidth
                required
                value={isAddingNewCategory ? ADD_NEW_CATEGORY_VALUE : faqForm.category}
                onChange={handleFaqCategorySelectChange}
                disabled={isSavingFaqForm}
                helperText="Select an existing category or add a new one"
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left !important',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  },
                }}
              >
                {faqCategories.map((category) => (
                  <MenuItem key={category} value={category} sx={{ justifyContent: 'flex-start' }}>
                    {category}
                  </MenuItem>
                ))}
                <MenuItem value={ADD_NEW_CATEGORY_VALUE} sx={{ justifyContent: 'flex-start', fontWeight: 600 }}>
                  + Add new category
                </MenuItem>
              </TextField>
              {isAddingNewCategory && (
                <TextField
                  label="New category name"
                  fullWidth
                  required
                  value={faqForm.category}
                  onChange={handleFaqFormFieldChange('category')}
                  disabled={isSavingFaqForm}
                  sx={{ mt: 2 }}
                  placeholder="Enter new category"
                />
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label="Question Type"
                fullWidth
                value={faqForm.question_type}
                onChange={handleFaqFormFieldChange('question_type')}
                disabled={isSavingFaqForm}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left !important',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  },
                }}
              >
                {FAQ_QUESTION_TYPES.map((type) => (
                  <MenuItem key={type} value={type} sx={{ justifyContent: 'flex-start' }}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField
                label="Question"
                fullWidth
                required
                value={faqForm.question}
                onChange={handleFaqFormFieldChange('question')}
                disabled={isSavingFaqForm}
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField
                label="Answer"
                fullWidth
                required={faqForm.question_type === 'static'}
                multiline
                minRows={3}
                value={faqForm.answer}
                onChange={handleFaqFormFieldChange('answer')}
                disabled={isSavingFaqForm || faqForm.question_type === 'dynamic'}
                helperText={
                  faqForm.question_type === 'dynamic'
                    ? 'Answer is disabled for dynamic questions'
                    : undefined
                }
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                label="Keyword"
                fullWidth
                required
                value={faqForm.keyword}
                onChange={handleFaqFormFieldChange('keyword')}
                disabled={isSavingFaqForm}
                helperText="Comma separated keywords"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                label="URL"
                fullWidth
                value={faqForm.url}
                onChange={handleFaqFormFieldChange('url')}
                disabled={isSavingFaqForm}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                label="URL Label"
                fullWidth
                value={faqForm.url_label}
                onChange={handleFaqFormFieldChange('url_label')}
                disabled={isSavingFaqForm}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                label="Image URL"
                fullWidth
                value={faqForm.image_url}
                onChange={handleFaqFormFieldChange('image_url')}
                disabled={isSavingFaqForm}
              />
            </Grid2>
            {faqForm.question_type === 'dynamic' && (
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  select
                  label="Webhook / Intent"
                  fullWidth
                  required
                  value={faqForm.webhook_id || ''}
                  onChange={(event) => handleFaqFormWebhookChange(event.target.value)}
                  disabled={isSavingFaqForm || isLoadingWebhooks}
                  helperText={
                    isLoadingWebhooks
                      ? 'Loading webhooks...'
                      : webhooks.length === 0
                        ? 'No webhooks found. Create a webhook first.'
                        : faqForm.webhook_name
                          ? `Selected intent: ${faqForm.webhook_name}`
                          : 'Select webhook for this dynamic FAQ'
                  }
                  sx={{
                    '& .MuiSelect-select': {
                      textAlign: 'left !important',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    },
                  }}
                >
                  {faqForm.webhook_id &&
                    !webhooks.some((webhook) => webhook.webhook_id === faqForm.webhook_id) && (
                      <MenuItem value={faqForm.webhook_id} sx={{ justifyContent: 'flex-start' }}>
                        {faqForm.webhook_name || faqForm.webhook_id}
                      </MenuItem>
                    )}
                  {webhooks.map((webhook) => (
                    <MenuItem
                      key={webhook.webhook_id}
                      value={webhook.webhook_id}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      {webhook.webhook_name} ({webhook.method})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid2>
            )}
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sub Questions
              </Typography>
              <Box
                sx={{
                  maxHeight: 180,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                {faqQuestions.filter((item) => item.question_id !== editingFaqQuestionId).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No other questions available.
                  </Typography>
                ) : (
                  faqQuestions
                    .filter((item) => item.question_id !== editingFaqQuestionId)
                    .map((item) => (
                      <FormControlLabel
                        key={item.question_id}
                        control={
                          <Checkbox
                            checked={faqForm.sub_questions_ids.includes(item.question_id)}
                            onChange={() => handleToggleFaqFormSubQuestion(item.question_id)}
                            disabled={isSavingFaqForm}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {item.question || item.question_id}
                          </Typography>
                        }
                        sx={{ display: 'flex', width: '100%', ml: 0, mb: 0.5 }}
                      />
                    ))
                )}
              </Box>
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFaqFormDialogOpen(false)}
            color="inherit"
            disabled={isSavingFaqForm}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveFaqForm}
            variant="contained"
            disabled={isSavingFaqForm}
            startIcon={
              isSavingFaqForm ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
            }
            sx={{
              background: isSavingFaqForm ? 'grey' : themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isSavingFaqForm
              ? editingFaqQuestionId
                ? 'Updating...'
                : 'Adding...'
              : editingFaqQuestionId
                ? 'Update FAQ'
                : 'Add FAQ'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(faqToDelete)}
        onClose={() => !isDeletingFaq && setFaqToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete FAQ Question</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this question?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>{faqToDelete?.question || faqToDelete?.question_id}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqToDelete(null)} color="inherit" disabled={isDeletingFaq}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteFaqQuestion}
            color="error"
            variant="contained"
            disabled={isDeletingFaq}
            startIcon={isDeletingFaq ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {isDeletingFaq ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => !isDeletingSelectedFaqs && setBulkDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Selected FAQ Questions</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{' '}
            <strong>
              {selectedFaqIds.length} selected question{selectedFaqIds.length === 1 ? '' : 's'}
            </strong>
            ?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteDialogOpen(false)}
            color="inherit"
            disabled={isDeletingSelectedFaqs}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSelectedFaqs}
            color="error"
            variant="contained"
            disabled={isDeletingSelectedFaqs || selectedFaqIds.length === 0}
            startIcon={
              isDeletingSelectedFaqs ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />
            }
          >
            {isDeletingSelectedFaqs ? 'Deleting...' : 'Delete Selected'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(webhookToDelete)}
        onClose={() => !isDeletingWebhook && setWebhookToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Webhook</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this webhook?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>{webhookToDelete?.webhook_name || webhookToDelete?.webhook_id}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebhookToDelete(null)} color="inherit" disabled={isDeletingWebhook}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteWebhook}
            color="error"
            variant="contained"
            disabled={isDeletingWebhook}
            startIcon={isDeletingWebhook ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {isDeletingWebhook ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={webhookTestDialogOpen}
        onClose={() => !isTestingWebhook && setWebhookTestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Test Webhook</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fill dynamic values for{' '}
            <strong>{webhookUnderTest?.webhook_name || webhookUnderTest?.webhook_id}</strong>
            {' '}({webhookUnderTest?.method || 'GET'}).
          </Typography>

          {webhookTestPlaceholders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No dynamic fields found. You can run the test directly.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              {webhookTestPlaceholders.map((placeholderKey) => (
                <TextField
                  key={placeholderKey}
                  label={getWebhookPlaceholderFieldName(placeholderKey)}
                  placeholder={`Enter ${getWebhookPlaceholderFieldName(placeholderKey)}`}
                  fullWidth
                  required
                  value={webhookTestValues[placeholderKey] || ''}
                  onChange={(event) =>
                    handleWebhookTestValueChange(placeholderKey, event.target.value)
                  }
                  disabled={isTestingWebhook}
                  helperText={`Template: {{${placeholderKey}}}`}
                />
              ))}
            </Box>
          )}

          {webhookTestResult && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Response
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={6}
                value={webhookTestResult}
                InputProps={{ readOnly: true }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: 13,
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setWebhookTestDialogOpen(false)}
            color="inherit"
            disabled={isTestingWebhook}
          >
            Close
          </Button>
          <Button
            onClick={handleRunWebhookTest}
            variant="contained"
            disabled={isTestingWebhook || isUpdatingWebhookStatus}
            startIcon={
              isTestingWebhook || isUpdatingWebhookStatus ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <PlayArrowIcon />
              )
            }
            sx={{
              background: isTestingWebhook || isUpdatingWebhookStatus ? 'grey' : themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isTestingWebhook
              ? 'Testing...'
              : isUpdatingWebhookStatus
                ? 'Updating status...'
                : 'Run Test'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={webhookActionAnchor}
        open={Boolean(webhookActionAnchor)}
        onClose={handleWebhookActionMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEditFromWebhookMenu}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteFromWebhookMenu} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={faqActionAnchor}
        open={Boolean(faqActionAnchor)}
        onClose={handleFaqActionMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleOpenAddSubQuestions}>
          <AccountTreeIcon fontSize="small" sx={{ mr: 1 }} />
          Add sub Questions
        </MenuItem>
        <MenuItem onClick={handleOpenAddIntent}>
          <PsychologyIcon fontSize="small" sx={{ mr: 1 }} />
          Add Intent
        </MenuItem>
        <MenuItem onClick={handleDeleteFromFaqMenu} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={subQuestionsDialogOpen}
        onClose={() => !isSubmittingFaqUpdates && setSubQuestionsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Sub Questions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select questions to attach under{' '}
            <strong>{faqActionTarget?.question || faqActionTarget?.question_id}</strong>.
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search questions..."
            value={subQuestionSearch}
            onChange={(event) => setSubQuestionSearch(event.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {(() => {
              const searchableQuestions = faqQuestions.filter(
                (item) => item.question_id !== faqActionTarget?.question_id
              );
              const searchTerm = subQuestionSearch.trim().toLowerCase();
              const filteredQuestions = searchTerm
                ? searchableQuestions.filter((item) => {
                    const haystack = [
                      item.question,
                      item.category,
                      item.answer,
                      item.question_id,
                      item.question_type,
                    ]
                      .join(' ')
                      .toLowerCase();
                    return haystack.includes(searchTerm);
                  })
                : searchableQuestions;

              if (searchableQuestions.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No other questions available to select.
                  </Typography>
                );
              }

              if (filteredQuestions.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No questions match your search.
                  </Typography>
                );
              }

              return filteredQuestions.map((item) => (
                <FormControlLabel
                  key={item.question_id}
                  control={
                    <Checkbox
                      checked={draftSubQuestionIds.includes(item.question_id)}
                      onChange={() => handleToggleDraftSubQuestion(item.question_id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{item.question || item.question_id}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.category || 'No category'}
                      </Typography>
                    </Box>
                  }
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, ml: 0 }}
                />
              ));
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubQuestionsDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSaveDraftSubQuestions} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={intentDialogOpen}
        onClose={() => !isSubmittingFaqUpdates && setIntentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Intent</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a webhook intent for{' '}
            <strong>{faqActionTarget?.question || faqActionTarget?.question_id}</strong>.
          </Typography>
          {isLoadingWebhooks ? (
            <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
              <CircularProgress size={20} />
              <Typography sx={{ ml: 1.5 }}>Loading webhooks...</Typography>
            </Box>
          ) : webhooks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No webhooks found. Create a webhook first.
            </Typography>
          ) : (
            <TextField
              select
              label="Webhook"
              fullWidth
              value={draftWebhookId}
              onChange={(event) => setDraftWebhookId(event.target.value)}
              sx={{
                mt: 1,
                '& .MuiSelect-select': {
                  textAlign: 'left !important',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                },
              }}
            >
              {webhooks.map((webhook) => (
                <MenuItem key={webhook.webhook_id} value={webhook.webhook_id} sx={{ justifyContent: 'flex-start' }}>
                  {webhook.webhook_name} ({webhook.method})
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntentDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveDraftIntent}
            variant="contained"
            disabled={!draftWebhookId || webhooks.length === 0}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdateChatBot;
